import { FormProvider } from 'react-hook-form';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import { classNames } from '@/utils';

import type { SubStepType } from '@/lib/signup/userInfos/subSteps';
import { subSteps } from '@/lib/signup/userInfos/subSteps';

import Button from '@/components/base/Button';
import InfoBanner from '@/components/base/InfoBanner';
import StickyFooter from '@/components/pages/signup/StickyFooter';

import { useUserInfosFormLogic } from './useUserInfosFormLogic';

export type { UserInfosFormType } from '@/lib/signup/userInfos/formBusinessRules';

type UserInfosFormProps = {
    currentSubStep: SubStepType;
    setCurrentSubStep: (arg: SubStepType) => void;
};

const UserInfosForm = ({ currentSubStep, setCurrentSubStep }: UserInfosFormProps) => {
    const { t } = useTranslation('signup_funnel');

    const {
        formMethods,
        isStepThreeComplete,
        isSubmitting,
        isValid,
        globalError,
        setGlobalError,
        onSubmit,
        goToSubStep
    } = useUserInfosFormLogic({ currentSubStep, setCurrentSubStep });

    return (
        <FormProvider {...formMethods}>
            <form className="flex w-full flex-1 flex-col justify-between space-y-8">
                <div className="flex flex-col space-y-8">
                    {globalError ? (
                        <InfoBanner
                            title={t('errors:error')}
                            message={globalError}
                            onClose={() => setGlobalError(null)}
                        />
                    ) : null}
                    <currentSubStep.form />
                </div>
                <StickyFooter
                    wrapperClassName={classNames({
                        'justify-between': currentSubStep.id !== 1,
                        'justify-end': currentSubStep.id === 1
                    })}>
                    {currentSubStep.id !== 1 ? (
                        <button
                            type="button"
                            disabled={isSubmitting}
                            onClick={() => {
                                goToSubStep(currentSubStep.id - 1);
                            }}
                            className="font-GMedium text-xs text-icoGray-500 underline">
                            {t('commons:back')}
                        </button>
                    ) : null}
                    <Button
                        disabled={!isValid || !isStepThreeComplete || isSubmitting}
                        isLoading={isSubmitting}
                        type="button"
                        data-testid="signup-next-button"
                        onClick={
                            currentSubStep.id === subSteps.length
                                ? onSubmit
                                : () => {
                                      goToSubStep(currentSubStep.id + 1);
                                  }
                        }>
                        {t('commons:next')}
                    </Button>
                </StickyFooter>
            </form>
        </FormProvider>
    );
};

export default UserInfosForm;
